import MarkdownIt from "markdown-it";
import { encodeTarget, isExternalHref, parseWikiLink } from "../vault/links.ts";

/** `[ ] ` / `[x] ` al principio de un ítem de lista. */
const TASK_RE = /^\[([ xX])\][ \t]+/;

/**
 * Listas de tareas (`- [ ] algo`), como en Obsidian/GitHub.
 *
 * Se hace en dos pasadas: antes de `inline` se quita el `[x] ` del texto (así
 * el parser nunca lo ve como un posible link de referencia) y después se
 * antepone el `<input type=checkbox>` a los hijos ya parseados. El documento en
 * disco no cambia: sigue siendo `- [x] algo`.
 *
 * Cada checkbox lleva `data-task-line` con la línea del `.md` que lo originó
 * (markdown-it la trae en `token.map`), que es lo que permite tildarlo desde la
 * vista previa y escribir de vuelta en la línea exacta, sin buscar por texto.
 *
 * Solo listas con viñeta: en una lista numerada el checkbox reemplazaría al
 * número, y el editor tampoco las trata como tarea. Mejor que las dos vistas
 * coincidan.
 */
function taskLists(md: MarkdownIt): void {
  md.core.ruler.before("inline", "task-lists-strip", (state) => {
    const { tokens } = state;
    const lists: string[] = [];
    for (let i = 0; i < tokens.length; i++) {
      const type = tokens[i].type;
      if (type === "bullet_list_open" || type === "ordered_list_open") lists.push(type);
      else if (type === "bullet_list_close" || type === "ordered_list_close") lists.pop();
      if (i < 2 || type !== "inline") continue;
      if (lists[lists.length - 1] !== "bullet_list_open") continue;
      if (tokens[i - 1].type !== "paragraph_open") continue;
      const item = tokens[i - 2];
      if (item.type !== "list_item_open") continue;
      const m = TASK_RE.exec(tokens[i].content);
      if (!m) continue;
      tokens[i].content = tokens[i].content.slice(m[0].length);
      tokens[i].meta = { ...tokens[i].meta, task: m[1] !== " ", line: item.map?.[0] ?? null };
      item.attrJoin("class", "task-list-item");
    }
    return true;
  });

  md.core.ruler.after("inline", "task-lists-box", (state) => {
    for (const token of state.tokens) {
      if (token.type !== "inline" || !token.meta || !("task" in token.meta)) continue;
      const box = new state.Token("html_inline", "", 0);
      const line: number | null = token.meta.line;
      // Contenido propio, no del documento: seguro con `html: false`.
      box.content =
        `<input class="task-checkbox" type="checkbox"` +
        `${token.meta.task ? " checked" : ""}` +
        `${line === null ? " disabled" : ` data-task-line="${line}"`}>`;
      token.children?.unshift(box);
    }
    return true;
  });
}

/**
 * Obsidian-style image sizing: `![alt|300](path)` renders the image 300px wide
 * and `![alt|300x200](path)` fixes both dimensions. The `|w[×xh]` suffix is read
 * from the alt text and stripped out of the rendered `alt` attribute.
 */
function imageSizes(md: MarkdownIt): void {
  md.renderer.rules.image = (tokens, idx, options, env, self) => {
    const token = tokens[idx];
    const alt = self.renderInlineAsText(token.children ?? [], options, env);
    const m = /^(.*?)\|(\d+)(?:[xX×](\d+))?$/.exec(alt);
    token.attrSet("alt", m ? m[1] : alt);
    if (m) {
      token.attrSet("width", m[2]);
      if (m[3]) token.attrSet("height", m[3]);
    }
    return self.renderToken(tokens, idx, options);
  };
}

/**
 * Ids en los encabezados, para que `nota.md#seccion` tenga a dónde llegar.
 *
 * El slug es el de GitHub —minúsculas, sin acentos, espacios a guiones— porque
 * es el que ya tienen en la cabeza los enlaces escritos a mano y el que genera
 * cualquier otro visor del mismo `.md`. Los repetidos llevan sufijo (`-1`,
 * `-2`) dentro de un mismo render, como allá.
 */
function headingAnchors(md: MarkdownIt): void {
  md.core.ruler.push("heading-anchors", (state) => {
    const used = new Map<string, number>();
    const { tokens } = state;
    for (let i = 0; i < tokens.length; i++) {
      if (tokens[i].type !== "heading_open") continue;
      const inline = tokens[i + 1];
      if (!inline || inline.type !== "inline") continue;
      const base = slugify(inline.content);
      if (!base) continue;
      const seen = used.get(base) ?? 0;
      used.set(base, seen + 1);
      tokens[i].attrSet("id", seen === 0 ? base : `${base}-${seen}`);
    }
    return true;
  });
}

/** El slug de un encabezado. Exportado porque quien navega a `#seccion` tiene
 *  que calcular el mismo id que se escribió al renderizar. */
export function slugify(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\p{L}\p{N}]+/gu, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * Marca los enlaces que apuntan adentro del vault con `data-internal`.
 *
 * Se hace acá, en el renderizador, y no en cada vista, porque hay tres
 * lectores del mismo HTML —la vista previa del documento, el panel de lectura
 * y el rasterizado de las tarjetas del canvas— y si cada uno decidiera por su
 * cuenta qué es interno, un enlace se vería (y se comportaría) distinto según
 * dónde se lo mire. El destino se deja intacto en el `href`: el `.md` sigue
 * siendo el de siempre y el atributo es sólo una marca para el click.
 */
function internalLinks(md: MarkdownIt): void {
  const base = md.renderer.rules.link_open;
  md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
    const href = tokens[idx].attrGet("href") ?? "";
    if (href && !isExternalHref(href)) tokens[idx].attrSet("data-internal", "");
    return base
      ? base(tokens, idx, options, env, self)
      : self.renderToken(tokens, idx, options);
  };
}

/**
 * Lee (y sólo lee) los `[[wikilinks]]` de un vault venido de Obsidian.
 *
 * Blanqo nunca escribe uno —ver `vault/links.ts`—, pero un vault importado
 * llegaría con todos sus enlaces muertos si el renderizador los tratara como
 * texto suelto. Se convierten al vuelo en el mismo `<a data-internal>` que
 * produce un enlace markdown, así que navegan por el mismo camino.
 */
function wikiLinks(md: MarkdownIt): void {
  md.inline.ruler.before("link", "wikilink", (state, silent) => {
    const { src, pos } = state;
    if (src.charCodeAt(pos) !== 0x5b /* [ */ || src.charCodeAt(pos + 1) !== 0x5b) return false;
    const end = src.indexOf("]]", pos + 2);
    if (end < 0) return false;
    const inner = src.slice(pos + 2, end);
    // Un `[[` sin cerrar en la misma línea, o vacío, es texto y no un enlace.
    if (!inner || inner.includes("\n") || inner.includes("[")) return false;
    if (!silent) {
      const { target, label } = parseWikiLink(inner);
      const open = state.push("link_open", "a", 1);
      open.attrSet("href", encodeTarget(target));
      const text = state.push("text", "", 0);
      text.content = label;
      state.push("link_close", "a", -1);
    }
    state.pos = end + 2;
    return true;
  });
}

const md = new MarkdownIt({
  html: false,
  linkify: true,
  breaks: true,
  typographer: true,
})
  .use(taskLists)
  .use(imageSizes)
  .use(headingAnchors)
  .use(wikiLinks)
  .use(internalLinks);

export function renderMarkdown(src: string): string {
  return md.render(src);
}

/**
 * Igual, pero para un fragmento que va DENTRO de una línea: no envuelve en
 * `<p>`. Lo usan las celdas de una tabla del editor, que se dibujan una por una.
 */
export function renderMarkdownInline(src: string): string {
  return md.renderInline(src);
}

/** First non-empty line, stripped of markdown markers — used for the LOD-far label. */
export function extractTitle(src: string): string {
  for (const raw of src.split("\n")) {
    const line = raw.trim();
    if (!line) continue;
    return line.replace(/^#+\s*/, "").replace(/[*_`>#-]/g, "").trim() || "(untitled)";
  }
  return "(empty)";
}
